import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Briefcase, Award, Clock, DollarSign, Tag } from "lucide-react";
import api from "../lib/api";
import { FlowGrid, FlowBadge } from "../components/FlowVisuals";

const CAT_LABELS = { content: "Content", design: "Design", automation: "Automation", development: "Development", research: "Research", customer_growth: "Growth", hiring: "Hiring", operations: "Ops", finance: "Finance", branding: "Branding", web_digital: "Web", local_business: "Local", personal: "Personal", general: "General" };

export default function DoerProfilePage() {
  const { doerId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/doers/profile/${doerId}`).then(r => setProfile(r.data)).catch(() => navigate("/")).finally(() => setLoading(false));
  }, [doerId, navigate]);

  if (loading) return (<div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><FlowGrid opacity={0.02} /><p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Loading profile...</p></div>);
  if (!profile) return null;

  const { stats } = profile;

  return (
    <div style={{ paddingTop: 84, minHeight: "100vh", position: "relative" }}>
      <FlowGrid opacity={0.02} />
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 64px", position: "relative", zIndex: 1 }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", marginBottom: 24 }}>
          <ArrowLeft size={14} /> Back
        </button>

        {/* Profile header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="doer-profile-header"
          style={{ background: "var(--bg-elevated)", borderRadius: 20, padding: "28px 32px", border: "1px solid var(--bg-neutral)", boxShadow: "0 4px 24px rgba(0,0,0,0.03)", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg, #005CEE, #8A4FFF)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.1rem", color: "#fff" }}>{profile.name?.charAt(0) || "?"}</span>
            </div>
            <div>
              <h1 data-testid="doer-name" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.5rem", color: "var(--text-main)", margin: 0 }}>{profile.name}</h1>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-muted)", margin: "4px 0 0" }}>Joined {new Date(profile.joined).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</p>
            </div>
          </div>

          {/* Stats grid */}
          <div data-testid="doer-stats" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { icon: Briefcase, label: "Completed", value: stats.tasks_completed, color: "var(--primary)" },
              { icon: Clock, label: "Active", value: stats.active_tasks, color: "var(--violet)" },
              { icon: DollarSign, label: "Earned", value: `${stats.total_earned.toLocaleString()}c`, color: "var(--primary)" },
              { icon: Award, label: "Specialties", value: stats.specialties.length, color: "var(--violet)" },
            ].map((s, i) => (
              <div key={i} style={{ padding: "14px 12px", borderRadius: 12, background: "var(--bg-soft)", textAlign: "center" }}>
                <s.icon size={16} color={s.color} style={{ marginBottom: 6 }} />
                <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: "1.1rem", color: "var(--text-main)" }}>{s.value}</div>
                <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", color: "var(--text-muted)", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Specialties */}
          {stats.specialties.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 16 }}>
              {stats.specialties.map(s => (
                <span key={s} style={{ padding: "3px 10px", borderRadius: 100, background: "var(--accent-subtle)", border: "1px solid rgba(0,92,238,0.08)", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", fontWeight: 600, color: "var(--accent-text)" }}>
                  {CAT_LABELS[s] || s}
                </span>
              ))}
            </div>
          )}
        </motion.div>

        {/* Portfolio */}
        <h2 data-testid="portfolio-heading" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--text-main)", marginBottom: 16 }}>
          Execution Portfolio
          <FlowBadge count={profile.portfolio.length} style={{ marginLeft: 10, verticalAlign: "middle" }} />
        </h2>

        {profile.portfolio.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem" }}>No completed tasks yet.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {profile.portfolio.map((task, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                data-testid={`portfolio-item-${i}`}
                style={{ padding: "14px 18px", borderRadius: 14, background: "var(--bg-elevated)", border: "1px solid var(--bg-neutral)", boxShadow: "0 2px 12px rgba(0,0,0,0.02)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: "0.85rem", color: "var(--text-main)" }}>{task.original_query}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.62rem", fontWeight: 700, color: "var(--primary)" }}>+{Math.round(task.estimated_payout || 0)}c</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Tag size={10} color="var(--text-muted)" />
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.68rem", color: "var(--text-muted)" }}>{CAT_LABELS[task.category] || task.category}</span>
                  {task.poster_name && <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", color: "var(--text-muted)" }}>for {task.poster_name}</span>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
